import { Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/internal/Subject';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { AddTicketModel } from 'src/sb-modules/Frontend-form-field-module/common/models/add-ticket-model.model';
import { FormComponent } from '../../components/form/form.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { NewLinePipe } from 'src/sb-tools/typescript/pipes/new-line.pipe';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { AttachmentsModalComponent } from '../attachments-modal/attachments-modal.component';
import { AttachmentType } from '@sb-tools/typescript/enums/attachment-type.enum';
import { MessageStorageService } from 'src/sb-modules/Frontend-form-field-module/common/services/message-storage.service';
import { FormFieldsValidationService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-fields-validation.service';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { WindowRef } from '@sb-tools/typescript/services/window-ref.service';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { environment } from '@environment';
import { ClientForm } from 'src/sb-modules/Frontend-form-field-module/common/models/client-form.model';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { NotificationsService, NotificationType } from 'angular2-notifications';
import { ClientField } from '@form-field-module/common/models/client-field.model';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { ForwardTicket } from '../../../core/models/forward-ticket.model';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { ForwardEntity } from '../../../core/models/forward-entity.model';
import { ForwardEntityTypeEnum } from '../../../core/enums/forward-entity-type.enum';
import { ForwardMessageService } from '../../../core/services/forward-message.service';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { FileViewComponent } from '../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { FilePreviewComponent } from '../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ClipboardModule } from 'ngx-clipboard';
import { RouterLink } from '@angular/router';
import { SlicePipe } from '@angular/common';
import { FilterContentAttachmentsPipe } from '../../pipes/filter-content-attachments.pipe';
import { FormsModule } from '@angular/forms';
import { AssignCategoryAndTagsService } from '@form-field-module/common/services/assign-category-and-tags.service';

@Component({
    selector: 'app-forward-message-modal',
    templateUrl: './forward-message-modal.component.html',
    styleUrls: ['./forward-message-modal.component.scss'],
    imports: [
        TranslateModule,
        TooltipModule,
        InlineSVGComponent,
        LoaderComponent,
        FormComponent,
        FileViewComponent,
        FilePreviewComponent,
        BsDropdownModule,
        ClipboardModule,
        ModalModule,
        RouterLink,
        SlicePipe,
        FilterContentAttachmentsPipe,
        FormsModule,
    ],
})
export class ForwardMessageModalComponent implements OnInit, OnDestroy {
    @ViewChild(FormComponent) formComponent: FormComponent;
    @ViewChild('notification') notification: TemplateRef<ElementRef>;

    @Input() forwardTicketData?: ForwardTicket; // pass when forward in new tab

    attachmentTypeEnum = AttachmentType;
    currentTicket: DetailedTicketDTO;
    entity: ForwardEntity | DetailedTicketDTO | ThreadElementDTO;
    entityType: ForwardEntityTypeEnum;
    fieldTypes = FieldType;
    form: ClientForm;
    formTypesEnum = FormType;
    forwardEntityTypeEnum = ForwardEntityTypeEnum;
    isAssignedToCreator: boolean;
    isNeedSecondBackdrop: boolean;
    isFormReady: boolean;
    isForwardingInNewTab: boolean;
    isForwardAttachmentOnly: boolean;
    isSaving: boolean;
    isThread: boolean;
    messageData;
    newTicketLink: string;
    step = 1;
    ticketId: number;

    private isHtmlEditor: boolean;
    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public formCommonDataService: FormCommonDataService,
        public modalRef: BsModalRef,
        public assignCategoryAndTagsService: AssignCategoryAndTagsService,
        private formFieldsValidationService: FormFieldsValidationService,
        private bsModalService: BsModalService,
        private formService: FormService,
        private notificationsService: NotificationsService,
        private ticketService: TicketService,
        private translateService: TranslateService,
        private newLinePipe: NewLinePipe,
        private forwardMessageService: ForwardMessageService,
        private messageStorageService: MessageStorageService,
        private windowRef: WindowRef,
    ) {}

    ngOnInit() {
        this.formCommonDataService.isMessageContentLoaded = true;

        if (this.forwardTicketData) {
            this.isForwardingInNewTab = true;
            this.currentTicket = this.forwardTicketData.currentTicket;
            this.entity = this.forwardTicketData.entity;
            this.entityType = this.forwardTicketData.entityType;
        }
        this.isForwardAttachmentOnly = this.entityType === ForwardEntityTypeEnum.attachment;
        this.isThread = this.entityType === ForwardEntityTypeEnum.ticket;
        this.isHtmlEditor =
            [TicketSource.form, TicketSource.email].includes(this.currentTicket.source) &&
            this.entityType !== ForwardEntityTypeEnum.attachment;
        this.initForwardingData();
    }

    backToForm() {
        this.step = 1;
        this.isFormReady = false;
        this.formService.isSubmitted = false;
        this.initForwardingData();
    }

    cancel() {
        if (this.isForwardingInNewTab) {
            window.close();
        } else {
            this.modalRef.hide();
        }
    }

    deleteGifAttachment(attachment: AttachmentBase) {
        const index = this.messageData.attachments.indexOf(attachment);
        this.messageData.attachments.splice(index, 1);
    }

    openAttachmentsModal() {
        this.isNeedSecondBackdrop = true;

        const initialState = {
            newAttachments: this.messageData.attachments,
            existedAttachmentsLength: this.messageData.attachments.length,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });

        this.bsModalService.onHide.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.isNeedSecondBackdrop = false;
        });
    }

    openFormInNewWindow() {
        const forwardTicketLink =
            this.windowRef.nativeWindow.location.origin +
            '/' +
            this.commonDataService.communityName +
            '/forward?ticketId=' +
            this.currentTicket.id +
            '&forwardType=' +
            this.entityType +
            this.getNewWindowUrlParams();
        const windowWidth = 1000;
        const right = 100;
        const left = window.screen.width - windowWidth - right;
        const windowFeatures = `popup,left=${left},top=100,width=1000,height=800`;
        window.open(forwardTicketLink, `Forward${this.currentTicket.id}`, windowFeatures);
    }

    send(isCloseTicket: boolean, isTicketSentAsLink: boolean) {
        this.formService.isSubmitted = true;
        this.isSaving = true;
        if (this.formComponent.ticketForm.valid) {
            const ticketModel = this.processTicketModel(isCloseTicket, isTicketSentAsLink);

            ticketModel.isAssignedToCreator = this.isAssignedToCreator;
            if (ticketModel.message) {
                ticketModel.message = ticketModel.message.replaceAll('<div></div>', '<div><br></div>');
            }

            ticketModel.ticketSource = DetailedTicketSource.formForward;

            this.assignCategoryAndTagsService
                .assignCategoryAndTags(ticketModel)
                .pipe(
                    takeUntil(this.unsubscriber),
                    switchMap((data) => this.ticketService.forward(data)),
                    finalize(() => {
                        this.isSaving = false;
                    }),
                )
                .subscribe((ticketId: number) => {
                    this.onTicketCreated(ticketId);
                    this.messageStorageService.clearDraft(true);
                });
        } else {
            setTimeout(() => {
                this.formFieldsValidationService.scrollToInvalidField('#newTicketForm', '#modalContainer');
                this.isSaving = false;
            }, 100);
        }
    }

    private getAllTicketAttachments(replies: ThreadElementDTO[], replyAttachments = []): ThreadElementDTO[] {
        replies.forEach((reply) => {
            replyAttachments = replyAttachments.concat(reply.attachments);
            replyAttachments = this.getAllTicketAttachments(reply.subReplies, replyAttachments);
        });
        return replyAttachments;
    }

    private getNewWindowUrlParams() {
        switch (this.entityType) {
            case ForwardEntityTypeEnum.reply: {
                return '&replyId=' + this.entity.id;
            }
            case ForwardEntityTypeEnum.ticketMessage: {
                return '&ticketMessage=' + true;
            }
            case ForwardEntityTypeEnum.attachment: {
                return (
                    '&attachmentUrl=' +
                    this.entity.attachments[0].url +
                    '&ownerId=' +
                    this.entity.attachments[0].ownerId +
                    '&ownerType=' +
                    this.entity.attachments[0].ownerType
                );
            }
            default:
                return '';
        }
    }

    private getTicketAttachments() {
        if (
            this.messageData.attachments.length === 1 &&
            this.messageData.attachments[0].attachmentType === AttachmentType.carousel
        ) {
            return this.messageData.attachments[0].attachments;
        } else {
            return this.messageData.attachments;
        }
    }

    private initFormData(form: ClientForm, isSortByTimeDesc?: boolean) {
        form.fields.forEach((formField: ClientField) => {
            switch (formField.type) {
                case this.fieldTypes.message: {
                    if (this.isThread) {
                        formField.value = this.forwardMessageService.getMessageValueForForwardThread(
                            this.messageData,
                            isSortByTimeDesc,
                        );
                    } else {
                        formField.value =
                            this.isHtmlEditor && this.messageData.message.html
                                ? this.messageData.message.html
                                : this.newLinePipe.transform(this.messageData.message.text);
                    }
                    break;
                }
                case this.fieldTypes.title: {
                    formField.value = this.translateService.instant('Fwd:') + ' ' + this.currentTicket.title;
                    break;
                }
            }
        });
    }

    private initForwardingData() {
        this.messageData = JSON.parse(JSON.stringify(this.entity));

        this.initMessageStorageData();

        this.formService
            .getForwardForm(this.currentTicket.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.form = data;

                if (this.isThread) {
                    const isFormEmailApi = [TicketSource.form, TicketSource.email].includes(this.currentTicket.source);

                    this.messageData.messageThread = this.forwardMessageService.processMessageThread(
                        this.messageData.messageThread,
                        isFormEmailApi,
                    );

                    this.initFormData(this.form, isFormEmailApi);

                    this.messageData.attachments = this.messageData.attachments.concat(
                        this.getAllTicketAttachments(this.messageData.messageThread, []),
                    );
                } else {
                    this.initFormData(this.form);
                }

                this.isFormReady = true;
            });
    }

    private initMessageStorageData() {
        // @TODO fix this logic for forward attachment only (now it use draft of first chat reply)
        if (this.isThread) {
            this.messageStorageService.ticketId = this.messageData.id;
        } else {
            this.messageStorageService.replyId = this.messageData.id;
            // for main ticket message replyId and ticketId values are equal
            this.messageStorageService.ticketId = this.messageData.ticketId;
        }
    }

    private onTicketCreated(ticketId: number): void {
        this.ticketId = ticketId;

        if (this.isForwardingInNewTab) {
            this.newTicketLink =
                environment.inboxUrl + '/' + this.commonDataService.communityName + '/tickets?ticketId=' + ticketId;
            this.step = 2;
        } else {
            this.modalRef.hide();

            this.notificationsService.html(
                this.notification,
                NotificationType.Success,
                {
                    timeOut: 5000,
                },
                'success',
            );
        }
    }

    private processTicketModel(isCloseTicket: boolean, isTicketSentAsLink: boolean) {
        const ticketModel = new AddTicketModel(
            this.form,
            isCloseTicket ? TicketStatus.completed : null,
            null,
            isTicketSentAsLink,
        );

        ticketModel.forwardedTicketId = this.currentTicket.id;
        ticketModel.attachments = this.getTicketAttachments();
        ticketModel.categoryId = this.currentTicket.category?.id;

        if (ticketModel.message) {
            // styles for blockquote in email view
            ticketModel.message = this.forwardMessageService.addInlineStylesToMessageHtmlString(ticketModel.message);
        }

        if (ticketModel.isSendFields && this.currentTicket.fields.length) {
            ticketModel.fields = ticketModel.fields.concat(
                this.currentTicket.fields.map((field) => {
                    return {
                        id: field.id,
                        value: field.value,
                    };
                }),
            );
        }

        return ticketModel;
    }

    ngOnDestroy() {
        this.messageStorageService.replyId = null;
        this.messageStorageService.ticketId = null;
        this.formService.isSubmitted = false;
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
